import React from 'react';
import PropTypes from 'prop-types';

import {
  PAYMENT_DEFAULT_BUTTON_LABEL,
  PAYMENT_DELETE_BUTTON_LABEL,
  PAYMENT_DRAWER_BUTTON_LABEL,
} from 'constant/paymentConstants';
import {
  StyledContainer,
  StyledDeleteButton,
  StyledDefaultButton,
} from './PaymentMethodButtons.styled';

/**
 * PaymentMethodButtons component.
 *
 * @property {boolean} [displayDefaultButton] - Whether to display the "Set as Default" button. (default: true)
 * @property {boolean} displayDeleteButton - Whether to display the "Delete" button.
 * @property {function} handleSetDefaultPayment - Callback function to handle setting the payment method as default.
 * @property {boolean} isDefault - Indicates if the payment method is a default payment.
 * @property {boolean} isSummaryPage - Indicates if the component is rendered on the summary-drawer page (otherwise is the payment page).
 * @property {function} toggleDeleteDialog - Callback function to toggle the delete confirmation dialog.
 */
const PaymentMethodButtons = ({
  displayDefaultButton,
  displayDeleteButton,
  handleSetDefaultPayment,
  isDefault,
  isSummaryPage,
  toggleDeleteDialog,
}) => {
  /**
   * Determines the primary button text based on whether the page is a summary page or not.
   */
  const defaultButtonText = isSummaryPage
    ? PAYMENT_DRAWER_BUTTON_LABEL
    : PAYMENT_DEFAULT_BUTTON_LABEL;

  /**
   * Determines the kind of button based on whether the page is a summary page or not.
   */
  const defaultButtonKind = isSummaryPage ? 'default' : 'primary';

  /**
   * When payment is default, no buttons are displayed.
   */
  if (isDefault) {
    return null;
  }
  return (
    <StyledContainer data-testid="BillingLicensing-PaymentMethodButtons">
      {displayDefaultButton && (
        <StyledDefaultButton
          data-testid="BillingLicensing-PaymentMethodButtons-DefaultButton"
          kind={defaultButtonKind}
          onClick={handleSetDefaultPayment}
        >
          {defaultButtonText}
        </StyledDefaultButton>
      )}
      {displayDeleteButton && (
        <StyledDeleteButton
          data-testid="BillingLicensing-PaymentMethodButtons-DeleteButton"
          kind="danger"
          onClick={toggleDeleteDialog}
        >
          {PAYMENT_DELETE_BUTTON_LABEL}
        </StyledDeleteButton>
      )}
    </StyledContainer>
  );
};

PaymentMethodButtons.defaultProps = {
  displayDefaultButton: true,
};

PaymentMethodButtons.propTypes = {
  displayDefaultButton: PropTypes.bool,
  displayDeleteButton: PropTypes.bool.isRequired,
  handleSetDefaultPayment: PropTypes.func.isRequired,
  isDefault: PropTypes.bool.isRequired,
  isSummaryPage: PropTypes.bool.isRequired,
  toggleDeleteDialog: PropTypes.func.isRequired,
};

export default PaymentMethodButtons;
